import { useState } from "react";
import { api, formatCurrency, formatDate, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PV_TYPES } from "@/lib/employeeConstants";

export const PVsTab = ({ employee, stats, pvs, year, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ pv_type: "vitesse", amount: 0, pv_date: "", description: "" });

  const handleSave = async () => {
    if (!form.pv_date) {
      toast.error("Date obligatoire");
      return;
    }
    try {
      await api.addEmployeePV(employee.id, { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success("PV ajouté");
      setDialogOpen(false);
      setForm({ pv_type: "vitesse", amount: 0, pv_date: "", description: "" });
      onRefresh();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const handleDelete = async (id) => {
    await api.deleteEmployeePV(id);
    toast.success("Supprimé");
    onRefresh();
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Contraventions (PV) {year}</CardTitle>
          <Button size="sm" onClick={() => setDialogOpen(true)} data-testid="add-pv-btn">
            <Plus className="h-4 w-4 mr-1" /> Ajouter
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
            {Object.entries(PV_TYPES).map(([k, label]) => (
              <div key={k} className="border rounded-lg p-3">
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="text-lg font-semibold">{formatCurrency(stats.pvs.by_type[k]?.total_amount || 0)}</div>
                <div className="text-xs text-muted-foreground">{stats.pvs.by_type[k]?.count || 0} PV</div>
              </div>
            ))}
            <div className="border rounded-lg p-3 bg-primary/5">
              <div className="text-xs text-muted-foreground">Total</div>
              <div className="text-lg font-bold">{formatCurrency(stats.pvs.total.total_amount)}</div>
              <div className="text-xs text-muted-foreground">{stats.pvs.total.count} PV</div>
            </div>
          </div>
          {pvs.length === 0 ? <p className="text-center text-muted-foreground py-4">Aucun PV.</p> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Montant</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pvs.map(p => (
                  <TableRow key={p.id}>
                    <TableCell>{PV_TYPES[p.pv_type]}</TableCell>
                    <TableCell>{formatDate(p.pv_date)}</TableCell>
                    <TableCell>{formatCurrency(p.amount)}</TableCell>
                    <TableCell>{p.description || "-"}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouveau PV</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <Select value={form.pv_type} onValueChange={v => setForm({ ...form, pv_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PV_TYPES).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.pv_date} onChange={e => setForm({ ...form, pv_date: e.target.value })} />
              </div>
              <div>
                <Label>Montant (€)</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} data-testid="save-pv-btn">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PVsTab;
